import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { ApolloError, gql, useMutation } from '@apollo/client';
import { APP_MODAL_ROUTES } from '@/navigators/routes';
import { AppModalStackParams } from '@/navigators/types';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { EmailInput } from '@/components/email-input';
import { FONTS, SCREEN_PADDING } from '@/styles';
import { FormInputContainer } from '@/components/form';
import {
  getLocalUserUpdatesFromEmployee,
  useAuthenticatedUserContext,
} from '@/contexts/user-context';
import { getMessageFromGQLError } from '@/utils/apollo-client';
import { isNotMaliPhoneNumber } from '@/utils/phone-number';
import { KeyboardAwareScrollView } from '@/components/with-keyboard-aware';
import { ModalContainer } from '@/components/modal-container';
import { PhoneNumberInput } from '@/components/phone-number-input';
import {
  RestaurantEmployeeFragment,
  UpdateEmployee_UpdateMutation,
  UpdateEmployee_UpdateMutationVariables,
} from '@/types';
import { StackScreenProps } from '@react-navigation/stack';
import { Text, View } from 'react-native';
import { TextInput } from '@/components/text-input';
import { Type } from '@/components/toast';
import { UpdateEmployeeValidationCodes } from '@platform/errors';
import {
  USER_CONTACT_INFO_FRAGMENT,
  USER_SUPPLIER_EMPLOYEE_OVERRIDES_FRAGMENT,
} from '@/contexts/user-context-queries';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToasts } from '@/contexts/toast-context';
import { useUpdateCachedRestaurantEmployee } from '@/hooks/use-restaurant-employees';

export const UPDATE_EMPLOYEE_OVERWRITE_CONTEXT = {
  ignoreGraphQLErrorCodes: [
    UpdateEmployeeValidationCodes.PHONE_NUMBER_EXISTS,
    UpdateEmployeeValidationCodes.UPDATE_EMPLOYEE_INVALID_EMAIL,
    UpdateEmployeeValidationCodes.LOGIN_EMAIL_EXISTS,
  ],
};

export const UPDATE_EMPLOYEE_MUTATION = gql`
  fragment UpdateEmployeeFields on employees {
    contact_info {
      ...UserContextUserContactInfo
    }
    name
    supplier_employee_overrides(
      where: { supplier_uuid: { _eq: $supplierUUID } }
    ) {
      ...UserContextUserSupplierEmployeeOverrides
    }
    uuid
  }

  mutation UpdateEmployee_Update(
    $employeeUUID: uuid!
    $supplierUUID: uuid!
    $name: String
    $phoneNumber: String
    $email: String
    $loginEmail: String
  ) {
    updateEmployee(
      employee_id: $employeeUUID
      supplier_id: $supplierUUID
      name: $name
      phone_number: $phoneNumber
      email: $email
      login_email: $loginEmail
    ) {
      employee {
        ...UpdateEmployeeFields
      }
    }
  }

  ${USER_CONTACT_INFO_FRAGMENT}
  ${USER_SUPPLIER_EMPLOYEE_OVERRIDES_FRAGMENT}
`;

export type Props = StackScreenProps<
  AppModalStackParams,
  APP_MODAL_ROUTES.UpdateEmployee
>;

export const UpdateEmployeeScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const {
    contactProtected,
    employeeUUID,
    initialEmail,
    initialLoginEmail,
    initialName,
    initialPhoneNumber,
    onUpdate,
    title = i18n.t('update_employee'),
  } = route.params;

  const updateCachedRestaurantEmployee = useUpdateCachedRestaurantEmployee();
  const {
    selectedRestaurantRelationship,
    updateUserLocally,
    user: { uuid: currentUserUUID },
  } = useAuthenticatedUserContext();

  const { bottom } = useSafeAreaInsets();
  const { showErrorToast, showToast } = useToasts();
  const [styles] = useStyles();

  const initialPhoneIsNotMaliPhone = isNotMaliPhoneNumber(initialPhoneNumber);
  const [name, setName] = useState(initialName);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(
    initialPhoneIsNotMaliPhone ? initialPhoneNumber : null,
  );
  const [contactEmail, setContactEmail] = useState(initialEmail || '');
  const [loginEmail, setLoginEmail] = useState(initialLoginEmail || '');

  const [updateEmployee, { client, loading }] = useMutation<
    UpdateEmployee_UpdateMutation,
    UpdateEmployee_UpdateMutationVariables
  >(UPDATE_EMPLOYEE_MUTATION, {
    context: UPDATE_EMPLOYEE_OVERWRITE_CONTEXT,
  });

  // Form data is invalid if there is no valid phone number or emails provided
  const formDataInvalid =
    !phoneNumber &&
    isNotMaliPhoneNumber(phoneNumber) &&
    !contactEmail &&
    !loginEmail;

  const contactProtectedCaption = contactProtected
    ? i18n.t(
        selectedRestaurantRelationship.employeeRole
          ? 'update_account.protected_contact.pepper'
          : 'update_account.protected_contact.distributor',
      )
    : null;

  return (
    <ModalContainer navigationHeaderProps={{ title }} shouldDismissKeyboard>
      <KeyboardAwareScrollView
        alwaysBounceVertical={false}
        contentContainerStyle={[
          styles.scrollView,
          { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
        ]}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.base}>
          <FormInputContainer>
            <TextInput
              containerStyle={styles.textInputContainer}
              label={name ? i18n.t('your_name') : undefined}
              onChangeText={setName}
              placeholder={i18n.t('your_name')}
              value={name}
            />
          </FormInputContainer>
          <FormInputContainer>
            <PhoneNumberInput
              caption={contactProtectedCaption}
              containerStyle={styles.textInputContainer}
              editable={!contactProtected}
              initialValue={phoneNumber || ''}
              onChangeText={setPhoneNumber}
              placeholder={i18n.t('phone_number')}
            />
          </FormInputContainer>
          <FormInputContainer>
            <EmailInput
              caption={contactProtectedCaption}
              containerStyle={styles.textInputContainer}
              editable={!contactProtected}
              label={loginEmail ? i18n.t('login_email') : undefined}
              onChangeText={setLoginEmail}
              placeholder={i18n.t('login_email')}
              value={loginEmail}
            />
          </FormInputContainer>
          <FormInputContainer>
            <EmailInput
              containerStyle={styles.textInputContainer}
              label={contactEmail ? i18n.t('communication_email') : undefined}
              onChangeText={setContactEmail}
              placeholder={i18n.t('email')}
              value={contactEmail}
            />
          </FormInputContainer>
        </View>
        {formDataInvalid ? (
          <View style={styles.bottomTextContainer}>
            <Text style={styles.errorText}>
              {i18n.t('toast.invalid_contact_info')}
            </Text>
          </View>
        ) : null}
        <Button
          disabled={formDataInvalid}
          loading={loading}
          onPress={async () => {
            if (!name) {
              showToast({
                content: i18n.t('toast.validation_error'),
                type: Type.WARNING,
              });
              return;
            } else if (!loginEmail && !phoneNumber) {
              showToast({
                content: i18n.t('missing_login_details'),
                type: Type.WARNING,
              });
              return;
            }

            try {
              const response = await updateEmployee({
                variables: {
                  email: contactEmail.trim() || null,
                  employeeUUID,
                  loginEmail: loginEmail.trim() || null,
                  name,
                  phoneNumber,
                  supplierUUID: selectedRestaurantRelationship.supplierUUID,
                },
              });

              const updatedEmployee = response.data?.updateEmployee.employee;
              if (updatedEmployee) {
                const employeeOverride =
                  updatedEmployee.supplier_employee_overrides[0];

                const updatedRestaurantEmployee: Partial<RestaurantEmployeeFragment> =
                  {
                    employee_contact_email:
                      (employeeOverride
                        ? employeeOverride.contact_emails[0]
                        : updatedEmployee.contact_info.email) ?? null,
                    employee_contact_phone_number:
                      employeeOverride?.contact_phone_numbers[0] ??
                      updatedEmployee.contact_info.phone_number,
                    employee_login_email:
                      updatedEmployee.contact_info.login_email,
                    employee_name:
                      employeeOverride?.name ?? updatedEmployee.name,
                  };

                updateCachedRestaurantEmployee(
                  client,
                  (cachedRestaurantEmployees) => {
                    return cachedRestaurantEmployees.map((re) => {
                      return re.employee_id === updatedEmployee.uuid
                        ? {
                            ...re,
                            ...updatedRestaurantEmployee,
                          }
                        : re;
                    });
                  },
                );

                onUpdate?.(updatedRestaurantEmployee);

                if (employeeUUID === currentUserUUID) {
                  updateUserLocally(
                    getLocalUserUpdatesFromEmployee(
                      updatedEmployee,
                      employeeOverride,
                    ),
                  );
                }
              }

              navigation.goBack();
            } catch (e) {
              if (e instanceof ApolloError && getMessageFromGQLError(e)) {
                showToast({
                  autoCloseTimeout: 0,
                  content: getMessageFromGQLError(e),
                  type: Type.NEGATIVE,
                });
              } else {
                showErrorToast();
              }
            }
          }}
          trackingData={{
            name: 'update_account.update_button',
          }}
        >
          {i18n.t('update')}
        </Button>
      </KeyboardAwareScrollView>
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
    marginBottom: 16,
  },
  bottomTextContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
    marginBottom: 8,
  },
  errorText: {
    ...FONTS.primarySemiBold,
    color: theme.colors.negative,
  },
  scrollView: {
    flexGrow: 1,
    paddingHorizontal: SCREEN_PADDING,
  },
  textInputContainer: {
    borderColor: theme.colors.stroke,
  },
}));
